import { CSSProperties } from 'react';
import { Box, Dialog, DialogActions, DialogContent } from '@mui/material';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { MDButton } from '../../../../Component/Common/Components/MDButton';
import colors from '../../../../assets/theme/base/colors';
import { BadgeTone } from './IndividualJustifications/recruitmentPromotionMovementsHelper';

interface DeviationRelativeToPlanModalProps {
    open: boolean;
    onClose: () => void;
}

const cell: CSSProperties = {
    padding: '10px 12px',
    fontSize: '0.8125rem',
    color: colors.dark.main,
    border: `1px solid ${colors.ui.border}`,
    verticalAlign: 'top',
};

const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: 1,
    py: 0.25,
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
} as const;

const meanings: { label: string; tone: BadgeTone; meaning: string }[] = [
    { label: 'Yes', tone: 'negative', meaning: 'The movement increased the gap - the workforce moved further from the EE Plan target for this demographic group and occupational level.' },
    { label: 'No', tone: 'positive', meaning: 'The movement was neutral or reduced the gap - aligned with the EE Plan target.' },
    { label: 'N/A', tone: 'neutral', meaning: 'No EE Plan target exists for this demographic group and occupational level combination.' },
];

export function DeviationRelativeToPlanModal({ open, onClose }: DeviationRelativeToPlanModalProps) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <CustomDialogTitle onClose={onClose}>Deviation Relative to EE Plan</CustomDialogTitle>
            <DialogContent dividers>
                <MDTypography variant="body2" paragraph>
                    This column shows whether each individual recruitment or promotion movement brought the workforce closer to or further from the EE Plan numerical targets at the time of the movement.
                </MDTypography>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        {meanings.map(({ label, tone, meaning }) => {
                            const palette = colors.deviationBadge[tone];
                            return (
                                <tr key={label}>
                                    <td style={{ ...cell, width: '88px' }}>
                                        <Box component="span" sx={{ ...pillSx, backgroundColor: palette.bg, color: palette.text }}>{label}</Box>
                                    </td>
                                    <td style={cell}>{meaning}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <MDTypography variant="caption" sx={{ display: 'block', mt: 2, color: colors.ui.secondaryText }}>
                    Deviation is assessed independently of priority rank. A P1 movement can still deviate from the plan, and a non-P1 movement can be aligned with the plan.
                </MDTypography>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="contained" color="primary" size="small">Got it</MDButton>
            </DialogActions>
        </Dialog>
    );
}
